
import React from 'react';
import { ArrowRight, BrainCircuit, TrendingUp, User, DollarSign, Euro, BarChart3, TrendingDown } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Link } from 'react-router-dom';

// Predefined particles to avoid hydration issues
const PARTICLES = [
  { id: 1, top: 20, left: 15, size: 8, duration: 10, delay: 0, opacity: 0.6 },
  { id: 2, top: 80, left: 85, size: 12, duration: 12, delay: 1, opacity: 0.7 },
  { id: 3, top: 35, left: 70, size: 6, duration: 8, delay: 2, opacity: 0.5 },
  { id: 4, top: 60, left: 25, size: 10, duration: 11, delay: 0.5, opacity: 0.65 },
  { id: 5, top: 10, left: 50, size: 8, duration: 9, delay: 1.5, opacity: 0.6 },
  { id: 6, top: 90, left: 40, size: 14, duration: 10, delay: 3, opacity: 0.7 },
  { id: 7, top: 45, left: 10, size: 6, duration: 8, delay: 2.5, opacity: 0.5 },
  { id: 8, top: 25, left: 90, size: 10, duration: 12, delay: 1, opacity: 0.65 },
  { id: 9, top: 70, left: 60, size: 8, duration: 9, delay: 3.5, opacity: 0.6 },
  { id: 10, top: 55, left: 80, size: 12, duration: 11, delay: 0.8, opacity: 0.7 },
  { id: 11, top: 15, left: 30, size: 6, duration: 10, delay: 2.2, opacity: 0.5 },
  { id: 12, top: 85, left: 20, size: 10, duration: 8, delay: 1.8, opacity: 0.65 },
  { id: 13, top: 40, left: 95, size: 8, duration: 9, delay: 3.2, opacity: 0.6 },
  { id: 14, top: 75, left: 45, size: 12, duration: 12, delay: 0.3, opacity: 0.7 },
  { id: 15, top: 30, left: 75, size: 6, duration: 11, delay: 2.8, opacity: 0.5 },
];

// Financial chart data points for creating line chart patterns
const CHART_PATHS = [
  {
    id: 1,
    path: "M20,80 Q40,60 60,70 T100,50 T140,45",
    top: 15,
    left: 5,
    width: 200,
    height: 80,
    duration: 6,
    delay: 0,
  },
  {
    id: 2,
    path: "M20,60 Q50,40 80,50 T120,30 T160,35",
    top: 60,
    left: 75,
    width: 220,
    height: 90,
    duration: 7,
    delay: 1.5,
  },
  {
    id: 3,
    path: "M15,70 Q35,50 55,60 T95,45 T135,40",
    top: 25,
    left: 78,
    width: 180,
    height: 70,
    duration: 8,
    delay: 3,
  },
  {
    id: 4,
    path: "M10,65 Q30,45 50,55 T90,40 T130,35",
    top: 70,
    left: 8,
    width: 190,
    height: 75,
    duration: 5,
    delay: 0.8,
  },
];

// Floating financial data points
const DATA_POINTS = [
  { id: 1, top: 25, left: 20, value: "₹2,456", trend: "up", duration: 5, delay: 0 },
  { id: 2, top: 45, left: 85, value: "$127.45", trend: "up", duration: 6, delay: 1 },
  { id: 3, top: 65, left: 15, value: "€89.23", trend: "down", duration: 7, delay: 2 },
  { id: 4, top: 35, left: 90, value: "+2.4%", trend: "up", duration: 5.5, delay: 0.5 },
  { id: 5, top: 65, left: 85, value: "NIFTY", trend: "up", duration: 6.5, delay: 1.5 },
  { id: 6, top: 15, left: 88, value: "BSE", trend: "up", duration: 7, delay: 2.5 },
  { id: 7, top: 85, left: 25, value: "-0.8%", trend: "down", duration: 5, delay: 1.2 },
  { id: 8, top: 50, left: 12, value: "GOLD", trend: "up", duration: 6, delay: 2.2 },
];

// Financial icons for floating effect
const FINANCIAL_ICONS = [
  { id: 1, Icon: DollarSign, top: 20, left: 15, size: 24, duration: 4, delay: 0 },
  { id: 2, Icon: Euro, top: 70, left: 90, size: 20, duration: 5, delay: 1 },
  { id: 3, Icon: BarChart3, top: 40, left: 8, size: 28, duration: 6, delay: 2 },
  { id: 4, Icon: TrendingUp, top: 80, left: 87, size: 26, duration: 4.5, delay: 0.5 },
  { id: 5, Icon: TrendingDown, top: 30, left: 92, size: 22, duration: 5.5, delay: 1.5 },
  { id: 6, Icon: BarChart3, top: 60, left: 12, size: 24, duration: 6, delay: 2.5 },
];

// Rotating text component with vertical flip animation
const RotatingText = () => {
  const words = [
    'stocks',
    'mutual funds', 
    'SIP',
    'PPF',
    'wealth building',
    'retirement',
    'real estate'
  ];

  return (
    <span className="rotating-text-container">
      {words.map((word, index) => (
        <span
          key={word}
          className={`rotating-text word-${index + 1}`}
        >
          {word}
        </span>
      ))}
    </span>
  );
};

const NewHeroSection = () => {
  return (
    <section className="relative min-h-screen md:min-h-[80vh] pt-36 md:pt-32 pb-20 md:pb-16 px-4 overflow-hidden bg-black">
      {/* Sophisticated Financial Background */}
      <div className="absolute inset-0 opacity-60 gpu-accelerated">
        {/* Base gradient blurs - Updated for black & gold theme */}
        <div className="absolute top-1/4 left-1/4 w-80 h-80 bg-[#9E7C47]/40 rounded-full filter blur-[60px] animate-pulse-slow"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#F9F1A9]/30 rounded-full filter blur-[60px] animate-pulse-slow"></div>
        
        {/* Financial Chart Patterns - Large blurred line charts */}
        <div className="absolute inset-0 overflow-hidden composite-layer">
          {CHART_PATHS.map((chart, index) => (
            <div
              key={chart.id}
              className={`absolute financial-element-optimized organic-float-${(index % 3) + 1}`}
              style={{
                top: `${chart.top}%`,
                left: `${chart.left}%`,
                width: `${chart.width}px`,
                height: `${chart.height}px`,
                animation: `financialChart ${chart.duration}s ease-in-out infinite`,
                animationDelay: `${chart.delay}s`,
                transform: 'translate3d(0, 0, 0)'
              }}
            >
              <svg
                width="100%"
                height="100%"
                viewBox={`0 0 ${chart.width} ${chart.height}`}
                className={`opacity-[0.15] filter blur-[2px] ${index % 2 === 0 ? 'chart-glow' : 'chart-glow-delayed'}`}
              >
                <defs>
                  <linearGradient id={`chartGradient${chart.id}`} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#9E7C47" />
                    <stop offset="50%" stopColor="#F9F1A9" />
                    <stop offset="100%" stopColor="#9E7C47" />
                  </linearGradient>
                </defs>
                <path
                  d={chart.path}
                  stroke={`url(#chartGradient${chart.id})`}
                  strokeWidth="3"
                  fill="none"
                  className="animate-chart-draw"
                />
              </svg>
            </div>
          ))}
        </div>

        {/* Floating Financial Data Points */}
        <div className="absolute inset-0 overflow-hidden composite-layer">
          {DATA_POINTS.map((point, index) => (
            <div
              key={point.id}
              className={`absolute text-xs font-bold financial-element-optimized px-2 py-1 rounded backdrop-blur-sm border border-white/10 organic-float-${(index % 3) + 1} ${
                point.trend === 'up' 
                  ? 'text-emerald-400 bg-emerald-500/10' 
                  : 'text-red-400 bg-red-500/10'
              } ${index % 3 === 0 ? 'data-pulse' : index % 3 === 1 ? 'data-pulse-delayed' : ''}`}
              style={{
                top: `${point.top}%`,
                left: `${point.left}%`,
                animation: `dataFloat ${point.duration}s ease-in-out infinite`,
                animationDelay: `${point.delay}s`,
                opacity: 0.30,
                transform: 'translate3d(0, 0, 0)'
              }}
            >
              {point.value}
            </div>
          ))}
        </div>

        {/* Floating Financial Icons */}
        <div className="absolute inset-0 overflow-hidden composite-layer">
          {FINANCIAL_ICONS.map((iconData, index) => {
            const { Icon } = iconData;
            return (
              <div
                key={iconData.id}
                className={`absolute financial-element-optimized text-white/20 organic-float-${(index % 3) + 1} ${
                  index > 3 ? 'financial-icons-mobile-hide' : ''
                } ${index % 3 === 0 ? 'icon-glow-pulse' : index % 3 === 1 ? 'icon-glow-pulse-delayed' : ''}`}
                style={{
                  top: `${iconData.top}%`,
                  left: `${iconData.left}%`,
                  animation: `iconFloat ${iconData.duration}s ease-in-out infinite`,
                  animationDelay: `${iconData.delay}s`,
                  transform: 'translate3d(0, 0, 0)'
                }}
              >
                <Icon size={iconData.size} />
              </div>
            );
          })}
        </div>

        {/* Subtle particle data flow */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden composite-layer">
          {PARTICLES.map((particle, index) => (
            <div 
              key={particle.id}
              className={`absolute bg-gradient-to-r from-[#9E7C47]/20 to-[#F9F1A9]/20 rounded-full financial-element-optimized organic-float-${(index % 3) + 1} ${
                index % 4 === 0 ? 'energy-pulse' : index % 4 === 1 ? 'energy-pulse-delayed' : ''
              }`}
              style={{
                top: `${particle.top}%`,
                left: `${particle.left}%`,
                width: `${particle.size}px`,
                height: `${particle.size}px`,
                animation: `particleFlow ${particle.duration}s linear infinite`,
                animationDelay: `${particle.delay}s`,
                opacity: particle.opacity * 0.6,
                transform: 'translate3d(0, 0, 0)'
              }}
            ></div>
          ))}
        </div>

        {/* Financial Grid Overlay */}
        <div className="absolute inset-0 opacity-[0.08]">
          <div 
            className="w-full h-full"
            style={{
              backgroundImage: `
                linear-gradient(rgba(158, 124, 71, 0.3) 1px, transparent 1px),
                linear-gradient(90deg, rgba(158, 124, 71, 0.3) 1px, transparent 1px)
              `,
              backgroundSize: '60px 60px',
              animation: 'gridShift 8s linear infinite'
            }}
          ></div>
        </div>
      </div>
      
      <div className="container mx-auto relative z-20">
        <div className="flex flex-col items-center text-center">
          
          {/* Main headline with rotating text */}
          <h1 className="text-[clamp(1.5rem,4vh,3rem)] md:text-3xl lg:text-4xl xl:text-5xl font-bold mb-4 leading-tight max-w-5xl animate-fade-in" style={{ animationDelay: '0.2s', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
            <span className="text-white">Institutional-grade insights at almost no cost for your</span>
            <br className="hidden md:block" />
            <span className="block text-center">
              <RotatingText />
            </span>
          </h1>
          
          {/* Subtext */}
          <p className="text-[clamp(1.125rem,2.5vh,1.5rem)] md:text-xl text-white/80 max-w-3xl mb-8 animate-fade-in font-medium" style={{ animationDelay: '0.4s', textShadow: '0 1px 3px rgba(0,0,0,0.4)' }}>
            Professional analysis powered by AI, validated by experts, and hyper-personalized for YOU!
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 w-full max-w-2xl justify-center animate-fade-in" style={{ animationDelay: '0.6s' }}>
            <Link to="/waitlist" className="flex-1 max-w-xs">
              <Button className="w-full h-14 bg-gradient-to-r from-[#9E7C47] via-[#F9F1A9] to-[#9E7C47] hover:from-[#8A6B3A] hover:via-[#E8E090] hover:to-[#8A6B3A] text-black text-[clamp(1rem,2vh,1.25rem)] font-bold group transition-all duration-300 hover:shadow-xl hover:shadow-[#9E7C47]/40 hover:scale-105">
                <User className="mr-2 h-5 w-5 group-hover:scale-110 transition-transform" />
                Join Waitlist
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            
            <Link to="/top-5-news" className="flex-1 max-w-xs">
              <Button variant="outline" className="w-full h-14 border-2 border-white/30 bg-white/5 hover:bg-white/10 text-white text-[clamp(1rem,2vh,1.25rem)] font-semibold group transition-all duration-300 hover:shadow-lg hover:border-white/50">
                <TrendingUp className="mr-2 h-5 w-5 group-hover:scale-110 transition-transform" />
                See Today's Market Brief
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewHeroSection;
